/**
 * Settings, How strictly agents follow your standards (ADR 0113, R-FN-4, R-FN-8): the pure part.
 * The mode, each rule's level and the two landing provenance choices, in plain words, plus the
 * helpers that group, search and change rule levels. Browser-safe, no React.
 */
import type { StandardsEnforcement, StandardsProvenancePolicy, StandardsRuleClass } from '@orgus/protocol';

export type EnforcementMode = StandardsEnforcement['mode'];

export const MODE_OPTIONS: { value: EnforcementMode; label: string; text: string }[] = [
  { value: 'enforce', label: 'Enforce', text: 'Required rules stop the edit' },
  { value: 'warn', label: 'Warn only', text: 'Agents are told, edits go ahead' },
  { value: 'off', label: 'Off', text: 'No checks while agents edit' },
];

/** The one dim sentence under the choice. */
export const MODE_MEANING: Record<EnforcementMode, string> = {
  enforce: 'An agent cannot save an edit that breaks a required rule; it is told why and fixes it.',
  warn: 'Agents see each broken rule as they edit, but nothing is stopped.',
  off: 'Nothing is checked while agents edit; the landing checks still run.',
};

export const LEVEL_OPTIONS: { value: StandardsRuleClass; label: string }[] = [
  { value: 'mandatory', label: 'Required' },
  { value: 'advisory', label: 'Advice' },
  { value: 'off', label: 'Off' },
];

export const POLICY_OPTIONS: { value: StandardsProvenancePolicy; label: string }[] = [
  { value: 'refuse', label: 'Refuse it' },
  { value: 'review', label: 'Ask a person' },
  { value: 'allow', label: 'Let it land' },
];

/** One rule the edit guard can check (the counted design rules). */
export interface EnforceableRule {
  id: string;
  /** The rule file's own level: error is required by default, warn is advice. */
  level: string;
  summary: string;
}

const GROUP_LABEL: Record<string, string> = {
  type: 'Type',
  shape: 'Shape and size',
  layout: 'Layout',
  content: 'Copy and formats',
  component: 'Components',
  feedback: 'Feedback',
  motion: 'Motion',
  layer: 'Layering',
  live: 'Live data',
  licensing: 'Plans',
  design: 'Colour and values',
};

export function groupLabel(id: string): string {
  const head = id.split('/')[0] ?? id;
  return GROUP_LABEL[head] ?? head.charAt(0).toUpperCase() + head.slice(1);
}

/** The level a rule has by default, before the org changes it. */
export function defaultLevel(r: EnforceableRule): StandardsRuleClass {
  return r.level === 'error' ? 'mandatory' : r.level === 'warn' ? 'advisory' : 'off';
}

/** The level set for a rule (ignoring the mode). */
export function levelOf(r: EnforceableRule, e: StandardsEnforcement): StandardsRuleClass {
  if (e.off.includes(r.id)) return 'off';
  if (e.mandatory.includes(r.id)) return 'mandatory';
  if (e.advisory.includes(r.id)) return 'advisory';
  return defaultLevel(r);
}

/** The three lists after one rule's level changes; a rule back at its default leaves every list. */
export function withLevel(e: StandardsEnforcement, r: EnforceableRule, level: StandardsRuleClass): Pick<StandardsEnforcement, 'mandatory' | 'advisory' | 'off'> {
  const drop = (xs: string[]) => xs.filter((x) => x !== r.id);
  const next = { mandatory: drop(e.mandatory), advisory: drop(e.advisory), off: drop(e.off) };
  if (level !== defaultLevel(r)) next[level] = [...next[level], r.id];
  return next;
}

/** Rules matching every word of the query (id or summary), grouped by area in first-seen order. */
export function groupRules(rules: EnforceableRule[], query: string): { label: string; rules: EnforceableRule[] }[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const hit = (r: EnforceableRule) => words.every((w) => `${r.id} ${r.summary} ${groupLabel(r.id)}`.toLowerCase().includes(w));
  const groups = new Map<string, EnforceableRule[]>();
  for (const r of rules) {
    if (!hit(r)) continue;
    const g = groupLabel(r.id);
    groups.set(g, [...(groups.get(g) ?? []), r]);
  }
  return [...groups].map(([label, list]) => ({ label, rules: list }));
}

/** The rules the guard checks: counted checks only, plus any id the setting names that the file lacks. */
export function enforceableRules(file: { rules?: { id: string; level: string; check: string; summary?: string }[] } | null, e: StandardsEnforcement | null): EnforceableRule[] {
  const list: EnforceableRule[] = (file?.rules ?? []).filter((r) => r.check === 'regex' || r.check === 'siblings').map((r) => ({ id: r.id, level: r.level, summary: r.summary ?? '' }));
  const known = new Set(list.map((r) => r.id));
  for (const id of e ? [...e.mandatory, ...e.advisory, ...e.off] : []) {
    if (!known.has(id)) {
      known.add(id);
      list.push({ id, level: 'off', summary: '' });
    }
  }
  return list;
}

/** "3 changed" for the Advanced summary. */
export function changedCount(e: StandardsEnforcement): number {
  return e.mandatory.length + e.advisory.length + e.off.length;
}
